import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { Grid } from '@material-ui/core';

import { showToast } from '@klpkonsern/ui';
import { useCurrentUser } from '@klp/auth';
import { ActionLink, Heading, Block } from '@klp/design';
import { Loader } from '@klp/design/Loader';
import { FileObject } from '@klp/file/classes';
import { useFileContext } from '@klp/file/context/FileContext';
import { Main } from '@klp/layout';

import {
    createEmptySakRequest,
    SakStatus,
    SakskategoriSimple,
    Saksmelding,
    VedleggSak,
} from '../classes';
import type { CreateSakRequest, ISakInput } from '../classes';
import { SakForm } from '../components';
import { SakRoutes } from '../config';
import { usePostSakMutation } from '../rtkApi';

export const SakAdd: React.FC = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const { clearQueues } = useFileContext();

    const [
        postSak,
        { data: saveResult, isLoading: sakSaving, isSuccess, isError },
    ] = usePostSakMutation();

    const { currentUser: user, currentUserIsLoading: userIsLoading } =
        useCurrentUser();
    const savedSakId = saveResult?.sak?.id;
    const hasUser = !!user;

    // Clear file queues on mount to reset state from previous session
    useEffect(() => {
        clearQueues();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSubmit = async (
        event: React.FormEvent<HTMLFormElement>,
        input: ISakInput,
    ) => {
        event.preventDefault();

        const vedleggSakList = [
            ...(input.sakImages || []),
            ...(input.sakAttachments || []),
        ].map((img: FileObject) =>
            VedleggSak.parseObject(FileObject.toBinaryFile(img)),
        );

        const melding = input.melding;

        const request: CreateSakRequest = {
            opprettetAv: input.opprettetAv,
            emne: input.emne,
            sakskategoriId: input.subkategoriId || input.sakskategoriId,
            kundeNummer: input.kundeNummer,
            status: SakStatus.Active.toString(),
            saksmeldingList: [
                Saksmelding.parseObject({ melding, vedleggSakList, sakId: '' }),
            ],
            ekstraSakskategoriList:
                input.supportkategoriId && input.supportkategoriType
                    ? [
                          new SakskategoriSimple({
                              id: input.supportkategoriId,
                              type: input.supportkategoriType,
                          }),
                      ]
                    : [],
        };

        try {
            await postSak(request).unwrap();
        } catch (error) {
            // Error handled by mutation
        }
    };

    const onCancel = () => {
        history.push(SakRoutes.moduleRoute());
    };

    // Redirect to single view or confirmation page after save
    useEffect(() => {
        if (!savedSakId) return;

        // Clear file upload queues before redirecting
        clearQueues();

        if (hasUser) {
            history.push(SakRoutes.single(savedSakId));
        } else {
            history.push(SakRoutes.confirmation());
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [savedSakId, hasUser]);

    useEffect(() => {
        if (isSuccess) {
            showToast({ body: t('sak.savingSuccess'), type: 'success' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isSuccess]);

    useEffect(() => {
        if (isError) {
            showToast({ body: t('sak.savingError'), type: 'error' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isError]);

    return (
        <Main>
            <Loader isLoading={userIsLoading}>
                {hasUser && (
                    <Block paddingBottom="bulky">
                        <ActionLink
                            actionType="linkBack"
                            translation="sak.backButton"
                            href={SakRoutes.moduleRoute()}
                        />
                    </Block>
                )}

                <Grid container={true}>
                    <Grid item={true} sm={10} md={10}>
                        <Heading translation="sak.addSak" />

                        {!userIsLoading && (
                            <SakForm
                                request={createEmptySakRequest()}
                                onSubmit={onSubmit}
                                onCancel={onCancel}
                                disabled={sakSaving}
                                authenticatedUser={user}
                            />
                        )}
                    </Grid>
                </Grid>
            </Loader>
        </Main>
    );
};
